import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { Badge } from "src/shadcn/ui/badge";
import { Button } from "src/shadcn/ui/button";
import { Input } from "src/shadcn/ui/input";
import { OptionDropDown } from "src/components/ui/inputs";
import { LoadingState } from "src/components";
import { VendorService, UserService } from "src/services";
import { CreationDateSortOptions, VendorColumnHeader } from "src/enums";
import { VendorFileType } from "src/enums/vendorFileType";
import { DateTimeRange, VendorsFiltersState } from "src/types";

const FILE_TYPE_OPTIONS: VendorFileType[] = [VendorFileType.CSV, VendorFileType.PDF];

const TOGGLE_BUTTON_BASE =
  "px-2 py-1.5 text-xs font-medium rounded-full border transition-all duration-150 flex items-center gap-1 justify-center";

const defaultFiltersState = (): VendorsFiltersState => ({
  vendorName: undefined,
  location: undefined,
  createdByUserEmail: undefined,
  portalOrderVendor: undefined,
  collectionOrderVendor: undefined,
  portalAccess: undefined,
  isProForma: undefined,
  fileTypes: [],
  emails: undefined
});

interface FilterVendorsProps {
  filters: VendorsFiltersState;
  onFilterChange: (filters: VendorsFiltersState) => void;
  dateTimeRange: DateTimeRange;
  onDateTimeRangeChange: (range: DateTimeRange) => void;
  sortOption: CreationDateSortOptions;
  onSortChange: (option: CreationDateSortOptions) => void;
  columnsToHide: VendorColumnHeader[];
  onColumnsToHideChange: (columns: VendorColumnHeader[]) => void;
  allColumns: VendorColumnHeader[];
  onClose?: () => void;
  isOpen?: boolean;
  vendorService?: VendorService;
  userService?: UserService;
}

const FilterVendors: React.FC<FilterVendorsProps> = ({
  filters,
  onFilterChange,
  dateTimeRange,
  onDateTimeRangeChange,
  sortOption,
  onSortChange,
  columnsToHide,
  onColumnsToHideChange,
  allColumns,
  onClose,
  isOpen,
  vendorService,
  userService
}) => {
  const [localFilters, setLocalFilters] = useState<VendorsFiltersState>(filters);
  const [localDateTimeRange, setLocalDateTimeRange] = useState<DateTimeRange>(dateTimeRange);
  const [localSortOption, setLocalSortOption] = useState<CreationDateSortOptions>(sortOption);
  const [dropdownOptions, setDropdownOptions] = useState<{
    vendorNames?: string[];
    locations?: string[];
    createdByEmails?: string[];
  }>({});
  const [loadingDropdownOptions, setLoadingDropdownOptions] = useState(false);
  const [dropdownOptionsFetchError, setDropdownOptionsFetchError] = useState(false);
  const [vendorNameInput, setVendorNameInput] = useState(filters.vendorName ?? "");
  const [locationInput, setLocationInput] = useState(filters.location ?? "");
  const [createdByInput, setCreatedByInput] = useState(filters.createdByUserEmail ?? "");
  const [emailsInput, setEmailsInput] = useState(filters.emails ?? "");
  const [isVendorDropdownOpen, setIsVendorDropdownOpen] = useState(false);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [isCreatedByDropdownOpen, setIsCreatedByDropdownOpen] = useState(false);

  const vendorInputRef = useRef<HTMLDivElement>(null);
  const locationInputRef = useRef<HTMLDivElement>(null);
  const createdByInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
      setLocalDateTimeRange(dateTimeRange);
      setLocalSortOption(sortOption);
      setVendorNameInput(filters.vendorName ?? "");
      setLocationInput(filters.location ?? "");
      setCreatedByInput(filters.createdByUserEmail ?? "");
      setEmailsInput(filters.emails ?? "");
    }
  }, [isOpen, filters, dateTimeRange, sortOption]);

  const fetchDropdownOptions = async () => {
    if (!vendorService && !userService) return;
    setDropdownOptionsFetchError(false);
    setLoadingDropdownOptions(true);
    try {
      const [vendorNames, locations, createdByEmails] = await Promise.all([
        vendorService?.getVendorNames() ?? [],
        vendorService?.getVendorLocations() ?? [],
        userService?.getUserEmails(["HEALF_OPERATIONS", "HEALF_OPERATIONS_MANAGER"]) ?? []
      ]);
      setDropdownOptions({ vendorNames, locations, createdByEmails });
    } catch (error) {
      console.error("Failed to load vendor filter options", error);
      setDropdownOptionsFetchError(true);
    } finally {
      setLoadingDropdownOptions(false);
    }
  };

  useEffect(() => {
    void fetchDropdownOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorService, userService]);

  type ToggleableArrayKey = "fileTypes";
  type ToggleableValueMap = {
    fileTypes: VendorFileType;
  };

  const toggleArrayValue = <K extends ToggleableArrayKey>(key: K, value: ToggleableValueMap[K]) => {
    setLocalFilters((prev) => {
      const currentValues = prev[key] as ToggleableValueMap[K][];
      const exists = currentValues.includes(value);
      const updatedValues = exists ? currentValues.filter((item) => item !== value) : [...currentValues, value];
      return { ...prev, [key]: updatedValues };
    });
  };

  type BooleanFilterKey = "portalOrderVendor" | "collectionOrderVendor" | "portalAccess" | "isProForma";
  const toggleBooleanFilter = (key: BooleanFilterKey, value: boolean) => {
    setLocalFilters((prev) => ({
      ...prev,
      [key]: prev[key] === value ? undefined : value
    }));
  };

  const handleVendorNameChange = (value?: string) => {
    const trimmed = value?.trim();
    setVendorNameInput(trimmed ?? "");
    setLocalFilters((prev) => ({ ...prev, vendorName: trimmed || undefined }));
  };

  const handleLocationChange = (value?: string) => {
    const trimmed = value?.trim();
    setLocationInput(trimmed ?? "");
    setLocalFilters((prev) => ({ ...prev, location: trimmed || undefined }));
  };

  const handleCreatedByChange = (value?: string) => {
    const trimmed = value?.trim();
    setCreatedByInput(value ?? "");
    setLocalFilters((prev) => ({ ...prev, createdByUserEmail: trimmed || undefined }));
  };

  const handleEmailsChange = (value: string) => {
    const trimmed = value.trim();
    setEmailsInput(value);
    setLocalFilters((prev) => ({ ...prev, emails: trimmed || undefined }));
  };

  const formatDateForInput = (date?: Date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const handleDateChange = (field: "start" | "end", value: string) => {
    if (!value) {
      setLocalDateTimeRange((prev) => ({ ...prev, [field]: undefined }));
      return;
    }

    const newDate = new Date(value);
    const maxEndDate = new Date();

    if (field === "start") {
      setLocalDateTimeRange((prev) => {
        const maxAllowedStart = prev.end ? new Date(prev.end) : undefined;
        const clampedStart = maxAllowedStart && newDate > maxAllowedStart ? maxAllowedStart : newDate;
        return {
          start: clampedStart,
          end: prev.end && clampedStart > prev.end ? clampedStart : prev.end
        };
      });
    } else {
      setLocalDateTimeRange((prev) => {
        const minAllowedEnd = prev.start ? new Date(prev.start) : undefined;
        let clampedEnd = newDate > maxEndDate ? maxEndDate : newDate;
        if (minAllowedEnd && clampedEnd < minAllowedEnd) {
          clampedEnd = minAllowedEnd;
        }
        return {
          ...prev,
          end: clampedEnd
        };
      });
    }
  };

  const handleApply = () => {
    onFilterChange(localFilters);
    onDateTimeRangeChange(localDateTimeRange);
    onSortChange(localSortOption);
    onClose?.();
  };

  const handleClearAll = () => {
    const clearedFilters = defaultFiltersState();
    const defaultRange = { start: undefined, end: undefined };

    setLocalFilters(clearedFilters);
    setVendorNameInput("");
    setLocationInput("");
    setCreatedByInput("");
    setEmailsInput("");
    setLocalDateTimeRange(defaultRange);
    setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC);

    onFilterChange(clearedFilters);
    onDateTimeRangeChange(defaultRange);
    onSortChange(CreationDateSortOptions.CREATION_DATE_DESC);
  };

  const countActiveFilters = useMemo(() => {
    let count = 0;
    if (localFilters.vendorName) count++;
    if (localFilters.location) count++;
    if (localFilters.createdByUserEmail) count++;
    if (localFilters.portalOrderVendor !== undefined) count++;
    if (localFilters.collectionOrderVendor !== undefined) count++;
    if (localFilters.portalAccess !== undefined) count++;
    if (localFilters.isProForma !== undefined) count++;
    if (localFilters.fileTypes.length) count++;
    if (localFilters.emails) count++;
    if (localDateTimeRange.start) count++;
    if (localDateTimeRange.end) count++;
    if (localSortOption !== CreationDateSortOptions.CREATION_DATE_DESC) count++;
    return count;
  }, [localFilters, localDateTimeRange, localSortOption]);

  if (loadingDropdownOptions) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingState type="LOADING" />
      </div>
    );
  }

  if (dropdownOptionsFetchError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <p className="text-sm text-secondary-600">Failed to load filter options.</p>
        <Button onClick={fetchDropdownOptions} variant="outline" size="sm">
          Retry
        </Button>
      </div>
    );
  }

  const renderBooleanFilter = (key: BooleanFilterKey, label: string) => (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">{label}</label>
      <div className="flex gap-2">
        {["Yes", "No"].map((option) => {
          const value = option === "Yes";
          const isActive = localFilters[key] === value;
          return (
            <button
              key={`${key}-${option}`}
              type="button"
              onClick={() => toggleBooleanFilter(key, value)}
              className={`${TOGGLE_BUTTON_BASE} ${
                isActive ? "bg-secondary-900 text-white border-secondary-900" : "bg-secondary-50 text-secondary-700"
              }`}
            >
              {option}
            </button>
          );
        })}
        {localFilters[key] !== undefined && (
          <button
            type="button"
            onClick={() => toggleBooleanFilter(key, localFilters[key] as boolean)}
            className="px-2 py-1.5 text-xs text-secondary-500 hover:text-secondary-900"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <div className="flex items-center gap-1.5">
            <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Filters</h3>
            {countActiveFilters > 0 && (
              <Badge variant="default" className="bg-secondary-900 text-white text-xs px-1.5 py-0 h-4">
                {countActiveFilters}
              </Badge>
            )}
          </div>
          {countActiveFilters > 0 && (
            <button
              onClick={handleClearAll}
              className="text-xs text-secondary-600 hover:text-secondary-900 transition-colors"
            >
              Clear All
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Vendor Name</label>
            <div className="relative" ref={vendorInputRef}>
              <Input
                value={vendorNameInput}
                onChange={(e) => {
                  handleVendorNameChange(e.target.value);
                  setIsVendorDropdownOpen(true);
                }}
                onFocus={() => setIsVendorDropdownOpen(true)}
                onBlur={() => setTimeout(() => setIsVendorDropdownOpen(false), 150)}
                placeholder="Search vendor name..."
                className="pr-20 text-sm h-9"
                disabled={!dropdownOptions.vendorNames?.length}
              />
              <div className="absolute right-1 top-1 flex gap-1">
                {vendorNameInput && (
                  <button
                    type="button"
                    onClick={() => handleVendorNameChange("")}
                    className="p-1 hover:bg-secondary-100 rounded transition-colors"
                  >
                    <X className="h-3.5 w-3.5 text-secondary-500" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsVendorDropdownOpen((prev) => !prev)}
                  className="p-1 hover:bg-secondary-100 rounded transition-colors"
                >
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-secondary-500 transition-transform ${
                      isVendorDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>
              <OptionDropDown
                value={vendorNameInput}
                onChange={(value) => {
                  handleVendorNameChange(value);
                  setIsVendorDropdownOpen(false);
                }}
                isOpen={isVendorDropdownOpen}
                options={dropdownOptions.vendorNames || []}
                triggerRef={vendorInputRef}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Location</label>
            <div className="relative" ref={locationInputRef}>
              <Input
                value={locationInput}
                onChange={(e) => {
                  handleLocationChange(e.target.value);
                  setIsLocationDropdownOpen(true);
                }}
                onFocus={() => setIsLocationDropdownOpen(true)}
                onBlur={() => setTimeout(() => setIsLocationDropdownOpen(false), 150)}
                placeholder="Search location..."
                className="pr-20 text-sm h-9"
                disabled={!dropdownOptions.locations?.length}
              />
              <div className="absolute right-1 top-1 flex gap-1">
                {locationInput && (
                  <button
                    type="button"
                    onClick={() => handleLocationChange("")}
                    className="p-1 hover:bg-secondary-100 rounded transition-colors"
                  >
                    <X className="h-3.5 w-3.5 text-secondary-500" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsLocationDropdownOpen((prev) => !prev)}
                  className="p-1 hover:bg-secondary-100 rounded transition-colors"
                >
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-secondary-500 transition-transform ${
                      isLocationDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>
              <OptionDropDown
                value={locationInput}
                onChange={(value) => {
                  handleLocationChange(value);
                  setIsLocationDropdownOpen(false);
                }}
                isOpen={isLocationDropdownOpen}
                options={dropdownOptions.locations || []}
                triggerRef={locationInputRef}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Created By</label>
            <div className="relative" ref={createdByInputRef}>
              <Input
                value={createdByInput}
                onChange={(e) => {
                  handleCreatedByChange(e.target.value);
                  setIsCreatedByDropdownOpen(true);
                }}
                onFocus={() => setIsCreatedByDropdownOpen(true)}
                onBlur={() => setTimeout(() => setIsCreatedByDropdownOpen(false), 150)}
                placeholder="Type or select an email..."
                className="pr-20 text-sm h-9"
                disabled={!dropdownOptions.createdByEmails?.length}
              />
              <div className="absolute right-1 top-1 flex gap-1">
                {createdByInput && (
                  <button
                    type="button"
                    onClick={() => handleCreatedByChange("")}
                    className="p-1 hover:bg-secondary-100 rounded transition-colors"
                  >
                    <X className="h-3.5 w-3.5 text-secondary-500" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsCreatedByDropdownOpen((prev) => !prev)}
                  className="p-1 hover:bg-secondary-100 rounded transition-colors"
                >
                  <ChevronDown
                    className={`h-3.5 w-3.5 text-secondary-500 transition-transform ${
                      isCreatedByDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>
              <OptionDropDown
                value={createdByInput}
                onChange={(value) => {
                  handleCreatedByChange(value);
                  setIsCreatedByDropdownOpen(false);
                }}
                isOpen={isCreatedByDropdownOpen}
                options={dropdownOptions.createdByEmails || []}
                triggerRef={createdByInputRef}
              />
            </div>
          </div>

          {renderBooleanFilter("portalOrderVendor", "Portal Orders")}
          {renderBooleanFilter("collectionOrderVendor", "Collection Orders")}
          {renderBooleanFilter("portalAccess", "Portal Access")}
          {renderBooleanFilter("isProForma", "Pro Forma")}

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">File Type</label>
            <div className="flex flex-wrap gap-2">
              {FILE_TYPE_OPTIONS.map((fileType) => {
                const active = localFilters.fileTypes.includes(fileType);
                return (
                  <button
                    key={fileType}
                    type="button"
                    onClick={() => toggleArrayValue("fileTypes", fileType)}
                    className={`${TOGGLE_BUTTON_BASE} ${
                      active ? "bg-secondary-900 text-white border-secondary-900" : "bg-secondary-50 text-secondary-700"
                    }`}
                  >
                    {fileType}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Vendor Emails</label>
            <Input
              value={emailsInput}
              onChange={(e) => handleEmailsChange(e.target.value)}
              placeholder="Contains email..."
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Created Date</label>
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1">
                <span className="text-xs text-secondary-500">Start</span>
                <Input
                  type="datetime-local"
                  value={formatDateForInput(localDateTimeRange.start)}
                  onChange={(e) => handleDateChange("start", e.target.value)}
                  max={formatDateForInput(localDateTimeRange.end || new Date())}
                  className="text-sm h-9"
                />
              </div>
              <div className="space-y-1">
                <span className="text-xs text-secondary-500">End</span>
                <Input
                  type="datetime-local"
                  value={formatDateForInput(localDateTimeRange.end)}
                  onChange={(e) => handleDateChange("end", e.target.value)}
                  min={localDateTimeRange.start ? formatDateForInput(localDateTimeRange.start) : undefined}
                  max={formatDateForInput(new Date())}
                  className="text-sm h-9"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Sort Order</label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_DESC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Newest
              </button>
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_ASC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_ASC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Oldest
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Show/Hide Columns</h3>
          <div className="flex items-center gap-2">
            {columnsToHide.length > 0 && (
              <button
                onClick={() => onColumnsToHideChange([])}
                className="text-xs text-secondary-600 hover:text-secondary-900 transition-colors"
              >
                Reset
              </button>
            )}
            <span className="text-xs text-secondary-600 font-medium">
              {allColumns.length - columnsToHide.length}/{allColumns.length}
            </span>
          </div>
        </div>
        <div className="space-y-1 max-h-44 overflow-y-auto pr-1 scrollbar-thin">
          {allColumns.map((column) => {
            const isVisible = !columnsToHide.includes(column);
            return (
              <button
                key={column}
                onClick={() => {
                  if (isVisible) {
                    onColumnsToHideChange([...columnsToHide, column]);
                  } else {
                    onColumnsToHideChange(columnsToHide.filter((c) => c !== column));
                  }
                }}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all ${
                  isVisible ? "bg-secondary-50 text-secondary-900 hover:bg-secondary-100" : "text-secondary-400"
                }`}
              >
                <span className="font-medium truncate">{column}</span>
                <Badge variant={isVisible ? "default" : "outline"} className="text-xs px-1.5 py-0 h-4 ml-2">
                  {isVisible ? "Visible" : "Hidden"}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>

      <div className="sticky bottom-0 pt-3 pb-1 bg-background border-t border-secondary-200 -mx-6 px-6 z-10">
        <Button
          onClick={handleApply}
          className="w-full bg-secondary-900 hover:bg-secondary-800 h-9 text-sm font-medium text-white"
        >
          Apply Changes
        </Button>
      </div>
    </div>
  );
};

export { FilterVendors };
